"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import dynamic from "next/dynamic"

import { isJsonObjectValue } from "@/types/json"

import { JsonCodeEditor } from "./JsonCodeEditor"

// MergeView constructs its editors against the DOM, so keep it off the server
// and out of the bundle for the callers that never open a diff.
const JsonMergeView = dynamic(
  () => import("./JsonMergeView").then((mod) => mod.JsonMergeView),
  { ssr: false },
)

type AccessibleName =
  | { ariaLabel: string; ariaLabelledBy?: string }
  | { ariaLabel?: string; ariaLabelledBy: string }

interface JsonEditorOwnProps {
  /** The JSON object value to edit */
  value: Record<string, unknown>
  /** Callback when valid JSON is entered (only called with valid JSON) */
  onChange: (value: Record<string, unknown>) => void
  /** Optional height for the editor (default: "300px") */
  height?: string
  /** Optional className for the container */
  className?: string
  /** Whether to allow empty JSON (default: true) */
  allowEmpty?: boolean
  /** Custom validation function - return error string or null if valid */
  validate?: (parsed: unknown) => string | null
  /** When true, show diff view (original = originalValue, modified = current) */
  useDiffEditor?: boolean
  /** Original value for diff comparison (used when useDiffEditor is true) */
  originalValue?: Record<string, unknown>
  /** Whether the editor is read-only */
  readOnly?: boolean
}

type JsonEditorProps = JsonEditorOwnProps & AccessibleName

const DEFAULT_HEIGHT = "300px"

function formatJson(value: Record<string, unknown>): string {
  return JSON.stringify(value, null, 2)
}

/**
 * A JSON editor that reads and writes a parsed object, validating on blur and
 * only calling `onChange` with JSON it could parse. Invalid text stays in the
 * editor so the user can fix it, and never reaches the parent.
 *
 * This is the object-level adapter over {@link JsonCodeEditor}; both run on
 * CodeMirror 6. Prefer `JsonCodeEditor` directly for form fields whose value is
 * text the user is midway through typing.
 */
export function JsonEditor({
  value,
  onChange,
  height = DEFAULT_HEIGHT,
  className = "",
  allowEmpty = true,
  validate,
  useDiffEditor = false,
  originalValue,
  readOnly = false,
  ...accessibleName
}: JsonEditorProps) {
  const lastSyncedValueRef = useRef<string>(formatJson(value))
  const [jsonValue, setJsonValue] = useState(() => formatJson(value))
  const [jsonError, setJsonError] = useState<string | null>(null)
  const isEditingJsonRef = useRef(false)

  // Sync editor value when value prop changes externally.
  // Skip while the user is editing, and skip while an error is showing, so a
  // parent re-render cannot discard text they are still fixing.
  useEffect(() => {
    const currentValueJson = formatJson(value)

    if (
      lastSyncedValueRef.current !== currentValueJson &&
      !isEditingJsonRef.current &&
      !jsonError
    ) {
      setJsonValue(currentValueJson)
      setJsonError(null)
      lastSyncedValueRef.current = currentValueJson
    }
  }, [value, jsonError])

  const handleJsonChange = useCallback((next: string) => {
    setJsonValue(next)
    setJsonError(null)
    isEditingJsonRef.current = true
  }, [])

  const handleJsonBlur = useCallback(() => {
    isEditingJsonRef.current = false

    if (!jsonValue.trim()) {
      if (allowEmpty) {
        onChange({})
        setJsonError(null)
        lastSyncedValueRef.current = formatJson({})
      } else {
        setJsonError("JSON cannot be empty")
      }
      return
    }

    try {
      const parsed: unknown = JSON.parse(jsonValue)

      if (validate) {
        const validationError = validate(parsed)
        if (validationError) {
          setJsonError(`Validation failed: ${validationError}`)
          return
        }
      }

      // An object is used directly; a primitive or array is wrapped so the
      // parent always receives the object shape its callback declares.
      const parsedValue: Record<string, unknown> = isJsonObjectValue(parsed)
        ? parsed
        : { value: parsed }

      const formatted = formatJson(parsedValue)
      if (formatted !== formatJson(value)) {
        onChange(parsedValue)
        setJsonValue(formatted)
        lastSyncedValueRef.current = formatted
      }
      setJsonError(null)
    } catch (e) {
      // Invalid JSON keeps the user's text and never reaches the parent.
      const errorMessage = e instanceof Error ? e.message : "Invalid JSON"
      setJsonError(`Invalid JSON - will not be saved: ${errorMessage}`)
    }
  }, [jsonValue, value, onChange, allowEmpty, validate])

  if (useDiffEditor) {
    return (
      <div className={`space-y-2 ${className}`}>
        <div
          className={`overflow-hidden rounded border ${
            jsonError ? "border-coval-danger-border" : "border-coval-border"
          }`}
        >
          <JsonMergeView
            original={originalValue ? formatJson(originalValue) : ""}
            modified={jsonValue}
            onChange={handleJsonChange}
            onBlur={handleJsonBlur}
            height={height}
            readOnly={readOnly}
            {...accessibleName}
          />
        </div>
        {jsonError && (
          <p className="text-xs text-coval-danger-text font-medium">
            {jsonError}
          </p>
        )}
      </div>
    )
  }

  return (
    <JsonCodeEditor
      className={className}
      value={jsonValue}
      onChange={handleJsonChange}
      onBlur={handleJsonBlur}
      height={height}
      readOnly={readOnly}
      error={jsonError}
      {...accessibleName}
    />
  )
}
